import { API_PREFIX, IDEMPOTENCY_HEADER } from '@recipes/contracts';
export class ApiClientError extends Error {
    status;
    error;
    constructor(status, error) {
        super(error.message);
        this.status = status;
        this.error = error;
        this.name = 'ApiClientError';
    }
    get code() {
        return this.error.code;
    }
}
export class NetworkError extends Error {
    constructor(cause) {
        super('Network request failed');
        this.name = 'NetworkError';
        this.cause = cause;
    }
}
function buildQuery(query) {
    if (!query)
        return '';
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === '')
            continue;
        params.set(key, Array.isArray(value) ? value.join(',') : String(value));
    }
    const s = params.toString();
    return s ? `?${s}` : '';
}
/**
 * Minimal fetch wrapper: JSON in/out, Bearer auth, single refresh-and-retry on 401,
 * idempotency keys, standard error envelope mapping.
 */
export class HttpClient {
    options;
    refreshing = null;
    constructor(options) {
        this.options = options;
    }
    get baseUrl() {
        return this.options.baseUrl.replace(/\/$/, '');
    }
    url(path, query) {
        const prefix = path.startsWith('/api/') || path.startsWith('/health') || path.startsWith('/metrics')
            ? ''
            : API_PREFIX;
        return `${this.baseUrl}${prefix}${path.startsWith('/') ? path : `/${path}`}${buildQuery(query)}`;
    }
    async request(method, path, body, options = {}) {
        const doFetch = this.options.fetch ?? fetch;
        const attempt = async (token) => {
            const headers = {
                Accept: 'application/json',
                ...(this.options.headers?.() ?? {}),
                ...(options.headers ?? {}),
            };
            if (body !== undefined && !(body instanceof FormData))
                headers['Content-Type'] = 'application/json';
            if (token)
                headers.Authorization = `Bearer ${token}`;
            if (options.idempotencyKey)
                headers[IDEMPOTENCY_HEADER] = options.idempotencyKey;
            try {
                return await doFetch(this.url(path, options.query), {
                    method,
                    headers,
                    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
                    credentials: 'include',
                    signal: options.signal,
                });
            }
            catch (error) {
                throw new NetworkError(error);
            }
        };
        let response = await attempt(await this.options.getAccessToken());
        if (response.status === 401 && !options.noRetry && this.options.refresh) {
            const fresh = await this.refreshOnce();
            if (fresh)
                response = await attempt(fresh);
        }
        if (!response.ok)
            throw await this.toError(response);
        if (response.status === 204)
            return undefined;
        const text = await response.text();
        return (text ? JSON.parse(text) : undefined);
    }
    async refreshOnce() {
        if (!this.refreshing) {
            this.refreshing = this.options.refresh().finally(() => {
                this.refreshing = null;
            });
        }
        return this.refreshing;
    }
    async toError(response) {
        let error = {
            code: 'INTERNAL_ERROR',
            message: response.statusText || 'Request failed',
        };
        try {
            const parsed = (await response.json());
            if (parsed && typeof parsed.code === 'string')
                error = parsed;
        }
        catch {
            // keep default
        }
        return new ApiClientError(response.status, error);
    }
    get(path, options) {
        return this.request('GET', path, undefined, options);
    }
    post(path, body, options) {
        return this.request('POST', path, body, options);
    }
    put(path, body, options) {
        return this.request('PUT', path, body, options);
    }
    patch(path, body, options) {
        return this.request('PATCH', path, body, options);
    }
    delete(path, body, options) {
        return this.request('DELETE', path, body, options);
    }
}
//# sourceMappingURL=http.js.map